/**
 * NavigationSandbox implementation — intercepts app navigation calls to prevent
 * Studio session destruction.
 *
 * Design: .planning/phases/151-sandbox-architecture-design/NAVIGATION-SANDBOX-DESIGN.md
 * Interface: ./navigation-sandbox.types.ts
 */

import type {
  NavigationSandbox,
  NavigationSandboxOptions,
  SandboxHistoryEntry,
  SandboxHistory,
  SandboxLocation,
  CreateNavigationSandbox,
} from './navigation-sandbox.types.js'

/**
 * Parse a URL string into { pathname, search, hash }.
 *
 * Examples:
 *   '/checkout' → { pathname: '/checkout', search: '', hash: '' }
 *   '/products?category=shoes' → { pathname: '/products', search: '?category=shoes', hash: '' }
 *   '/page#section-1' → { pathname: '/page', search: '', hash: '#section-1' }
 *   '/products?cat=shoes#top' → { pathname: '/products', search: '?cat=shoes', hash: '#top' }
 */
function parseUrl(url: string): { pathname: string; search: string; hash: string } {
  let pathname = url
  let search = ''
  let hash = ''

  // Extract hash first
  const hashIdx = pathname.indexOf('#')
  if (hashIdx !== -1) {
    hash = pathname.slice(hashIdx) // includes '#'
    pathname = pathname.slice(0, hashIdx)
  }

  // Extract search from what remains
  const searchIdx = pathname.indexOf('?')
  if (searchIdx !== -1) {
    search = pathname.slice(searchIdx) // includes '?'
    pathname = pathname.slice(0, searchIdx)
  }

  // Normalize empty pathname
  if (pathname === '') pathname = '/'

  return { pathname, search, hash }
}

/**
 * Creates a new NavigationSandbox instance.
 *
 * The sandbox intercepts all navigation calls that would otherwise mutate real
 * window.history or window.location, routing them into an in-sandbox stack.
 * Studio's SPA routing and session are never affected by app navigation.
 */
export const createNavigationSandbox: CreateNavigationSandbox = (
  options?: NavigationSandboxOptions
): NavigationSandbox => {
  const initialPathname = options?.initialPathname ?? '/'

  // ─── Internal state ────────────────────────────────────────────────────────
  let stack: SandboxHistoryEntry[] = [{ state: null, url: initialPathname }]
  let index = 0
  let destroyed = false
  const internalTarget = new EventTarget()

  // ─── SandboxLocation ──────────────────────────────────────────────────────
  // Mutable object — pathname/search/hash are updated by history methods.
  const parsed = parseUrl(initialPathname)
  const location: SandboxLocation = {
    pathname: parsed.pathname,
    search: parsed.search,
    hash: parsed.hash,
    get href(): string {
      return location.pathname + location.search + location.hash
    },
    assign(url: string): void {
      history.pushState(null, '', url)
    },
    replace(url: string): void {
      history.replaceState(null, '', url)
    },
  }

  // ─── Helper: update SandboxLocation from a url string ─────────────────────
  function updateLocation(url: string): void {
    const p = parseUrl(url)
    location.pathname = p.pathname
    location.search = p.search
    location.hash = p.hash
  }

  // ─── SandboxHistory ───────────────────────────────────────────────────────
  const history: SandboxHistory = {
    get state(): unknown {
      return stack[index]?.state ?? null
    },
    get length(): number {
      return stack.length
    },
    pushState(state: unknown, _title: string, url: string): void {
      if (destroyed) return
      // Truncate forward history
      stack = stack.slice(0, index + 1)
      stack.push({ state, url })
      index = stack.length - 1
      updateLocation(url)
      options?.onNavigationAttempt?.(url, 'pushState')
    },
    replaceState(state: unknown, _title: string, url: string): void {
      if (destroyed) return
      stack[index] = { state, url }
      updateLocation(url)
      options?.onNavigationAttempt?.(url, 'replaceState')
    },
    back(): void {
      if (destroyed || index <= 0) return
      index--
      const entry = stack[index]
      updateLocation(entry.url)
      options?.onNavigationAttempt?.(entry.url, 'back')
      internalTarget.dispatchEvent(
        new PopStateEvent('popstate', { state: entry.state, bubbles: false })
      )
    },
    forward(): void {
      if (destroyed || index >= stack.length - 1) return
      index++
      const entry = stack[index]
      updateLocation(entry.url)
      options?.onNavigationAttempt?.(entry.url, 'forward')
    },
  }

  // ─── NavigationSandbox facade ─────────────────────────────────────────────
  const sandbox: NavigationSandbox & { dispatchToSandbox: (event: Event) => void } = {
    get location(): SandboxLocation {
      return location
    },
    get history(): SandboxHistory {
      return history
    },
    openExternal(url: string, opts?: { onNavigate?: (url: string) => void }): void {
      // In sandbox mode: call opt callbacks but never navigate the browser
      opts?.onNavigate?.(url)
      options?.onNavigationAttempt?.(url, 'openExternal')
    },
    print(): void {
      // No-op in edit mode — printing the Studio shell is unwanted
      options?.onNavigationAttempt?.('', 'print')
    },
    reset(): void {
      stack = [{ state: null, url: '/' }]
      index = 0
      destroyed = false
      location.pathname = '/'
      location.search = ''
      location.hash = ''
    },
    destroy(): void {
      stack = []
      index = 0
      destroyed = true
      // internalTarget can be GC'd — no explicit cleanup needed
    },
    /**
     * Dispatch an event on the internal EventTarget.
     * Used by EventSandbox integration — not part of NavigationSandbox interface.
     * AppSandbox wires this internally.
     */
    dispatchToSandbox(event: Event): void {
      internalTarget.dispatchEvent(event)
    },
  }

  return sandbox
}
